import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Volume2, Snowflake, Sun, Droplets } from "lucide-react";
import { FadeIn, MaskedLines, Overline, Chapter } from "@/components/Motion";
import Marquee from "@/components/Marquee";
import EuropeMap from "@/components/EuropeMap";
import AIMeasureDemo from "@/components/AIMeasureDemo";
import CTABand from "@/components/CTABand";
import { IMAGES, PRODUCTS, WHY_ACTIVE, DISTRIBUTOR_REASONS } from "@/data/content";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-white" data-testid="home-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 sm:pt-44">
        <motion.div style={{ y: textY }} className="relative z-[3]">
          <FadeIn>
            <div className="flex items-center gap-3">
              <span className="w-2 h-2 bg-signal rounded-full" />
              <Overline>European Stairlift Manufacturing Platform</Overline>
            </div>
          </FadeIn>
          <h1 className="mt-8 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter uppercase leading-[0.98] text-slate-950 max-w-5xl">
            <MaskedLines immediate lines={["The Quietest", "Stairlift", "In The World"]} />
          </h1>
          <FadeIn delay={0.5}>
            <p className="mt-7 max-w-xl text-base md:text-lg text-slate-600 leading-relaxed">
              Powered by <span className="text-slate-950 font-medium">CDRIVE™</span> Brushless Technology.
              Protected by <span className="text-slate-950 font-medium">WSAFE™</span> Outdoor Technology.
              Designed for Europe.
            </p>
          </FadeIn>
          <FadeIn delay={0.65}>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact?type=distributor"
                data-testid="hero-cta-distributor"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-obsidian text-white text-sm font-medium px-8 py-4 hover:bg-signal transition-colors duration-300"
              >
                Become Distributor <ArrowUpRight size={15} />
              </Link>
              <Link
                to="/oem"
                data-testid="hero-cta-oem"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 text-slate-900 text-sm font-medium px-8 py-4 hover:border-slate-950 transition-colors duration-300"
              >
                OEM / ODM Solutions
              </Link>
            </div>
          </FadeIn>
        </motion.div>

        <FadeIn delay={0.4} className="relative z-[2] -mt-4 sm:-mt-10">
          <div className="spotlight-frame mt-12 sm:mt-16 rounded-2xl overflow-hidden border border-slate-900/10">
            <motion.img
              src={IMAGES.hero}
              alt="ACTIVE stairlift — premium product render"
              style={{ y: imgY, scale: 1.08 }}
              className="w-full h-[340px] sm:h-[540px] object-cover will-change-transform"
              data-testid="hero-image"
            />
          </div>
          <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2">
            {["38 dB rated", "CDRIVE™ Brushless", "WSAFE™ Outdoor", "Made in Europe"].map((b) => (
              <span key={b} className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                {b}
              </span>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

function WhyActive() {
  return (
    <section className="bg-white py-24 sm:py-32" data-testid="why-active">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Chapter index="01" label="Why ACTIVE" />
        <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-950 max-w-3xl leading-[1.05]">
          <MaskedLines lines={["Not another manufacturer.", "A manufacturing platform."]} />
        </h2>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-6 gap-4">
          {WHY_ACTIVE.map((c, i) => (
            <FadeIn
              key={c.title}
              delay={(i % 3) * 0.08}
              className={c.big ? "md:col-span-3" : "md:col-span-2"}
            >
              <div
                data-testid={`why-card-${i}`}
                className={`group h-full rounded-2xl border p-7 transition-all duration-300 hover:scale-[1.01] ${
                  c.dark
                    ? "bg-obsidian border-white/10 text-white noise-dark"
                    : "bg-[#F8F9FA] border-slate-200/80 hover:border-signal/40"
                }`}
              >
                <p className={`font-mono text-[10px] tracking-[0.25em] uppercase mb-6 ${c.dark ? "text-signal" : "text-slate-400"}`}>
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className={`font-display text-lg sm:text-xl font-semibold tracking-tight ${c.dark ? "text-white" : "text-slate-950"}`}>
                  {c.title}
                </h3>
                <p className={`mt-2 text-sm leading-relaxed ${c.dark ? "text-slate-400" : "text-slate-600"}`}>
                  {c.text}
                </p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductFamily() {
  return (
    <section className="bg-[#F8F9FA] py-24 sm:py-32 border-y border-slate-200/70" data-testid="product-family">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Chapter index="02" label="Product Family" />
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-950 leading-[1.05]">
            <MaskedLines lines={["Three machines.", "Every staircase."]} />
          </h2>
          <Link
            to="/products"
            data-testid="family-compare-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 hover:text-signal transition-colors"
          >
            Compare the range <ArrowRight size={14} />
          </Link>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {PRODUCTS.map((p, i) => (
            <FadeIn key={p.slug} delay={i * 0.1}>
              <Link
                to={`/products/${p.slug}`}
                data-testid={`family-card-${p.slug}`}
                className="group block rounded-2xl bg-white border border-slate-200/80 overflow-hidden hover:border-signal/40 transition-all duration-300 hover:scale-[1.01]"
              >
                <div className="overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-7">
                  <div className="flex items-center justify-between">
                    <p className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">{p.code}</p>
                    <div className="flex gap-1.5">
                      {p.tech.map((t) => (
                        <span key={t} className="font-mono text-[9px] tracking-[0.15em] uppercase text-signal border border-signal/30 rounded-full px-2 py-0.5">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                  <h3 className="mt-4 font-display text-xl font-semibold tracking-tight text-slate-950">{p.name}</h3>
                  <p className="mt-1.5 text-sm text-slate-600">{p.tagline}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-950 group-hover:text-signal transition-colors">
                    Explore <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

function CDriveSection() {
  const benefits = ["Ultra Silent", "Brushless Motor", "No Brushes", "Minimal Maintenance", "Long Life", "Smooth Ride", "Reduced Vibration", "Premium Comfort", "Energy Efficient", "Professional Reliability"];
  return (
    <section className="bg-obsidian noise-dark py-24 sm:py-32" data-testid="cdrive-section">
      <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <Chapter index="03" label="CDRIVE™ Brushless Technology" light />
            <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white leading-[1.05]">
              <MaskedLines lines={["Brushless Performance.", "Absolute Silence."]} />
            </h2>
            <FadeIn delay={0.15}>
              <div className="mt-10 flex items-end gap-5">
                <div className="relative">
                  <motion.span
                    className="absolute -left-3 top-2 w-2 h-2 rounded-full bg-signal"
                    animate={{ opacity: [1, 0.3, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  <span className="font-display text-7xl sm:text-8xl font-bold tracking-tighter text-white">38</span>
                </div>
                <div className="pb-2">
                  <p className="font-mono text-[11px] tracking-[0.25em] text-signal uppercase">dB measured</p>
                  <p className="text-sm text-slate-400 mt-1">Quieter than a whisper. Louder than nothing.</p>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.25}>
              <div className="mt-9 flex flex-wrap gap-2">
                {benefits.map((b) => (
                  <span key={b} className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-300 border border-white/15 rounded-full px-3.5 py-1.5 hover:border-signal/60 hover:text-white transition-colors duration-200">
                    {b}
                  </span>
                ))}
              </div>
            </FadeIn>
            <FadeIn delay={0.35}>
              <Link
                to="/technologies/cdrive"
                data-testid="cdrive-explore-link"
                className="mt-10 inline-flex items-center gap-2 text-sm font-medium text-white hover:text-signal transition-colors"
              >
                Explore CDRIVE™ <ArrowRight size={15} />
              </Link>
            </FadeIn>
          </div>
          <FadeIn delay={0.2}>
            <div className="spotlight-frame rounded-2xl overflow-hidden border border-white/10">
              <img src={IMAGES.cdrive} alt="CDRIVE™ brushless motor unit" loading="lazy" className="w-full h-[420px] object-cover" />
            </div>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
              CDRIVE™ drive unit · machined aluminium · zero brushes
            </p>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}

function DistributorReasons() {
  return (
    <section className="bg-white py-24 sm:py-32" data-testid="distributor-reasons">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <Chapter index="04" label="Partnership" />
              <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-950 leading-[1.05]">
                <MaskedLines lines={["Why distributors", "choose ACTIVE"]} />
              </h2>
              <FadeIn delay={0.2}>
                <p className="mt-6 text-base text-slate-600 leading-relaxed max-w-md">
                  One platform, one factory, one partner — structured so distributors win on margin, product and support simultaneously.
                </p>
                <Link
                  to="/contact?type=distributor"
                  data-testid="reasons-cta"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-obsidian text-white text-sm font-medium px-7 py-3.5 hover:bg-signal transition-colors duration-300"
                >
                  Become an ACTIVE Distributor <ArrowUpRight size={15} />
                </Link>
              </FadeIn>
            </div>
          </div>
          <div className="lg:col-span-7">
            {DISTRIBUTOR_REASONS.map((r, i) => (
              <FadeIn key={r.title} delay={Math.min(i * 0.04, 0.3)}>
                <div className="group flex items-baseline gap-6 py-5 border-b border-slate-200/80 first:border-t" data-testid={`reason-${i}`}>
                  <span className="font-mono text-[11px] text-signal tracking-[0.15em] w-7 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="flex-1 flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-8">
                    <h3 className="font-display text-lg font-semibold tracking-tight text-slate-950 sm:w-56 shrink-0 group-hover:text-signal transition-colors">
                      {r.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{r.text}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function DealerNetwork() {
  const platformRows = [
    { label: "Headquarters", value: "Milan, Italy" },
    { label: "R&D Center", value: "Suzhou, China" },
    { label: "Manufacturing", value: "Pujiang, China" },
    { label: "Distributor Territories", value: "Open across Europe" },
  ];
  return (
    <section className="bg-[#F8F9FA] py-24 sm:py-32 border-y border-slate-200/70" data-testid="dealer-network">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Chapter index="05" label="Headquarters" />
        <div className="max-w-2xl">
          <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-950 leading-[1.05]">
            <MaskedLines lines={["Headquartered in Milan.", "Building Europe's network."]} />
          </h2>
          <FadeIn delay={0.15}>
            <p className="mt-6 text-base text-slate-600 leading-relaxed">
              From our Milan headquarters, ACTIVE is onboarding one distribution leader per territory across Europe — with factory support, marketing assets and the full dealer platform behind every partner.
            </p>
          </FadeIn>
        </div>
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-5">
          <FadeIn delay={0.1} className="lg:col-span-7">
            <div className="relative rounded-2xl bg-white border border-slate-200/80 p-4 sm:p-6">
              <div className="absolute top-5 right-5 z-[3] rounded-xl border border-slate-200 bg-white/90 backdrop-blur px-4 py-3" data-testid="map-legend">
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-400 mb-2">Legend</p>
                <div className="flex items-center gap-2.5 mb-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-signal" />
                  <span className="text-[13px] font-medium text-slate-800">ACTIVE HQ — Milan</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#EDF1F5] border border-slate-300" />
                  <span className="text-[13px] text-slate-500">Territories open</span>
                </div>
              </div>
              <EuropeMap />
            </div>
          </FadeIn>
          <div className="lg:col-span-5 flex flex-col gap-5">
            <FadeIn delay={0.2}>
              <div className="rounded-2xl bg-obsidian noise-dark border border-white/10 p-8 flex-1">
                <p className="relative z-[2] font-mono text-[10px] uppercase tracking-[0.25em] text-signal">The Platform</p>
                <div className="relative z-[2] mt-6">
                  {platformRows.map((r, i) => (
                    <div
                      key={r.label}
                      className="flex items-baseline justify-between gap-6 py-4 border-b border-white/10 first:border-t"
                      data-testid={`platform-row-${i}`}
                    >
                      <span className="font-display text-base sm:text-lg font-semibold tracking-tight text-white">{r.label}</span>
                      <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 text-right">{r.value}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to="/contact?type=distributor"
                  data-testid="network-cta"
                  className="relative z-[2] mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-signal text-white text-sm font-medium px-7 py-3.5 hover:bg-white hover:text-obsidian transition-colors duration-300"
                >
                  Become our next distributor <ArrowUpRight size={15} />
                </Link>
              </div>
            </FadeIn>
            <FadeIn delay={0.3}>
              <div className="flex flex-wrap gap-3">
                {["48h enquiry response", "Territory exclusivity", "Launch marketing included"].map((t) => (
                  <span key={t} className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 border border-slate-300 rounded-full px-3.5 py-1.5 bg-white">
                    {t}
                  </span>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>
      </div>
    </section>
  );
}

function FactoryPlatform() {
  const pillars = [
    { title: "OEM Manufacturing", text: "Manufacture under your customer brand — product, documentation, packaging.", icon: Sun },
    { title: "ODM Development", text: "Complete product development from industrial design to mass production.", icon: Droplets },
    { title: "European Rail Manufacturing", text: "Premium European rail production. Future MODULAR RAIL™ technology.", icon: Snowflake },
    { title: "AI Dealer Platform", text: "CRM, marketing automation, technical support and AIMEASURE™ photo surveys.", icon: Volume2 },
  ];
  return (
    <section className="bg-obsidian noise-dark py-24 sm:py-32" data-testid="factory-platform">
      <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Chapter index="07" label="ACTIVE Factory Platform" light />
        <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white leading-[1.05] max-w-3xl">
          <MaskedLines lines={["More than a manufacturer.", "Your complete industrial platform."]} />
        </h2>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {pillars.map((p, i) => (
            <FadeIn key={p.title} delay={i * 0.08}>
              <div className="group h-full rounded-2xl border border-white/10 bg-surface/60 p-7 hover:border-signal/50 transition-colors duration-300" data-testid={`pillar-${i}`}>
                <p.icon size={20} className="text-signal" strokeWidth={1.5} />
                <h3 className="mt-5 font-display text-lg font-semibold tracking-tight text-white">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.text}</p>
              </div>
            </FadeIn>
          ))}
        </div>
        <FadeIn delay={0.25}>
          <div className="mt-12 spotlight-frame rounded-2xl overflow-hidden border border-white/10">
            <img src={IMAGES.factory} alt="ACTIVE European rail manufacturing facility" loading="lazy" className="w-full h-[300px] sm:h-[440px] object-cover" />
          </div>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
            European rail production · CNC bending · powder coating · final assembly
          </p>
        </FadeIn>
      </div>
    </section>
  );
}

function AIMeasureSection() {
  return (
    <section className="bg-white py-24 sm:py-32" data-testid="aimeasure-home">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Chapter index="06" label="AIMEASURE™ — Coming Soon" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-4">
            <h2 className="font-display text-2xl sm:text-4xl font-medium tracking-tight text-slate-950 leading-[1.05]">
              <MaskedLines lines={["A staircase photo", "becomes a rail"]} />
            </h2>
            <FadeIn delay={0.15}>
              <p className="mt-6 text-base text-slate-600 leading-relaxed">
                AIMEASURE™ turns smartphone photographs into production-ready rail geometry. No laser scanner, no expensive equipment — just drag the divider and watch the AI at work.
              </p>
              <Link
                to="/technologies#aimeasure-demo"
                data-testid="aimeasure-home-link"
                className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-slate-950 hover:text-signal transition-colors"
              >
                Explore AIMEASURE™ <ArrowRight size={15} />
              </Link>
            </FadeIn>
          </div>
          <div className="lg:col-span-8">
            <FadeIn delay={0.2}>
              <AIMeasureDemo />
            </FadeIn>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />
      <WhyActive />
      <ProductFamily />
      <CDriveSection />
      <DistributorReasons />
      <DealerNetwork />
      <AIMeasureSection />
      <FactoryPlatform />
      <CTABand />
    </>
  );
}
